import { useCallback, useMemo, useState } from "react";
import {
  type AccessFilter, ALL_ACCESS, toggleAccessFilter,
  accessAllowed, getNum, getStr, formatNumber,
  segColor, detectAccessPresence,
  type Row,
} from "./segmentHelpers.ts";
import { BarChart, type BarDatum } from "../../components/chart/charts.tsx";
import { DataGrid } from "../../components/dataGrid/dataGrid.tsx";
import type { ColumnDescription } from "../../types/session.ts";
import styles from "./segmentStatisticsPage.module.scss";

const GRID_NAME = "segmentKioskDrilldown";

const SEGMENT_FLAGS = '{"Airport":9,"Education":13,"Hospital":10,"Hotel":11,"Industry":6,"Office":7,"Other":2,"Retail":5,"Sport Center":12,"Stadium":3,"Transit Hub":8,"Workplace":4,"Unknown":0}';

function buildSegmentFilterCol(defaultId: number): ColumnDescription[] {
  return [{
    ColumnName: "SegmentID",
    ColumnFormat: "",
    Header: "Segment",
    DisplayType: "",
    ToolTip: "",
    FilterType: "FilterComboBox",
    FilterText: "Segment Name",
    FilterSettings: `Single|NOALL|DEFAULT=${defaultId}|Flags=${SEGMENT_FLAGS}`,
    LinkID: 0,
    LinkView: "",
    DefaultWidth: 0,
  }];
}

type Rank = "top" | "bottom";

export function KioskDrilldownTab({ defaultSegmentId = 9, hiddenColumns, hiddenFilters, highlightColumn, highlightValue, onReady }: { defaultSegmentId?: number; hiddenColumns?: string[]; hiddenFilters?: string[]; highlightColumn?: string; highlightValue?: string; onReady?: () => void } = {}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [filter, setFilter] = useState<AccessFilter>(ALL_ACCESS);
  const [rank, setRank] = useState<Rank>("top");

  const handleDataLoaded = useCallback((cols: string[], gridRows: Record<string, unknown>[]) => {
    setColumns(cols);
    setRows(gridRows as Row[]);
    onReady?.();
  }, [onReady]);

  const segmentFilterCol = useMemo(() => buildSegmentFilterCol(defaultSegmentId), [defaultSegmentId]);

  const cols = columns;

  const presence = useMemo(
    () => detectAccessPresence(rows, cols),
    [rows, cols],
  );

  const filtered = useMemo(() => {
    return rows.filter(r =>
      accessAllowed(getStr(r, cols, "AccessLevelName", "AccessLevel", "Access Level"), filter)
    );
  }, [rows, cols, filter]);

  const { spvData, wasteData, totalKiosks } = useMemo(() => {
    // Build per-kiosk metrics
    const spvRows: { label: string; extRef: string; rps: number; sessions: number; revenue: number }[] = [];
    const wasteRows: { label: string; extRef: string; val: number }[] = [];

    for (const row of filtered) {
      const storeName = getStr(row, cols, "sStoreName", "StoreName", "Store Name");
      const extRef = getStr(row, cols, "sExternalReference", "ExternalReference", "External Reference");
      const label = storeName || extRef || `Kiosk ${getStr(row, cols, "KioskID", "Kiosk ID")}`;
      const sessions = getNum(row, cols, "TotalMerchSessions", "Total Merch Sessions");
      const revenue = getNum(row, cols, "TotalRevenue", "Total Revenue");
      const wasteVal = Math.abs(getNum(row, cols, "TotalWaste", "Total Waste"));

      if (sessions > 0) {
        spvRows.push({ label, extRef, rps: revenue / sessions, sessions, revenue });
      }
      wasteRows.push({ label, extRef, val: wasteVal });
    }

    // Sort by rev/session descending
    spvRows.sort((a, b) => b.rps - a.rps);
    const totalKiosks = spvRows.length;

    // Pick top or bottom 10
    let chosenSpv: typeof spvRows;
    if (spvRows.length <= 10) {
      chosenSpv = spvRows;
    } else if (rank === "top") {
      chosenSpv = spvRows.slice(0, 10);
    } else {
      chosenSpv = spvRows.slice(-10).reverse();
    }

    const spvData: BarDatum[] = chosenSpv.map((d, i) => ({
      label: d.label,
      value: +d.rps.toFixed(2),
      color: segColor(i),
      tooltip: `${d.label}\nExternal Ref: ${d.extRef}\nRevenue/Merch Visit: ${formatNumber(d.rps)}\nMerch Visits: ${formatNumber(d.sessions)}\nRevenue: ${formatNumber(d.revenue)}`,
    }));

    // Waste: always top 10 (worst)
    wasteRows.sort((a, b) => b.val - a.val);
    const chosenWaste = wasteRows.slice(0, 10);
    const wasteData: BarDatum[] = chosenWaste.map((d, i) => ({
      label: d.label,
      value: d.val,
      color: segColor(i),
      tooltip: `${d.label}\nExternal Ref: ${d.extRef}\nWaste Count: ${formatNumber(d.val)}`,
    }));

    return { spvData, wasteData, totalKiosks };
  }, [filtered, cols, rank]);

  const hasChartData = rows.length > 0;

  const rankLabel = totalKiosks <= 10 ? `All (${totalKiosks})` : rank === "top" ? "Top 10" : "Bottom 10";

  return (
    <>
      {hasChartData && <>
      {/* Access filter pills */}
      <div className={styles.pillRow}>
        {(["all", "public", "semi", "priv"] as const).map(kind => {
          const labels: Record<string, string> = { all: "All", public: "Public", semi: "Semi-Public", priv: "Private" };
          const vis: Record<string, boolean> = { all: presence.public || presence.semi || presence.priv, public: presence.public, semi: presence.semi, priv: presence.priv };
          if (!vis[kind]) return null;
          return (
            <button
              key={kind}
              type="button"
              className={filter[kind] ? styles.pillActive : styles.pill}
              onClick={() => setFilter(prev => toggleAccessFilter(prev, kind))}
            >
              {labels[kind]}
            </button>
          );
        })}
      </div>

      {/* Top/Bottom toggle */}
      <div className={styles.toggleGroup}>
        <button
          type="button"
          className={rank === "top" ? styles.toggleBtnActive : styles.toggleBtn}
          onClick={() => setRank("top")}
        >
          Top 10
        </button>
        <button
          type="button"
          className={rank === "bottom" ? styles.toggleBtnActive : styles.toggleBtn}
          onClick={() => setRank("bottom")}
        >
          Bottom 10
        </button>
      </div>

      <div className={styles.separator} />

      {/* Charts */}
      <div className={styles.chartRow}>
        {spvData.length > 0 && (
          <div className={styles.chartCard}>
            <h4>
              Net Revenue / Merch Visit by Kiosk ({rankLabel})
              <span className={styles.infoIcon} style={{ marginLeft: 6 }}>
                ℹ<span className={styles.infoTip}>
                  Revenue per merchandise visit for each kiosk (store name).
                  Top 10 = highest; Bottom 10 = lowest.
                </span>
              </span>
            </h4>
            <BarChart
              data={spvData}
              width={Math.max(500, spvData.length * 78)}
              height={320}
            />
          </div>
        )}

        {wasteData.length > 0 && (
          <div className={styles.chartCard}>
            <h4>
              Waste per Kiosk (Store Name) — Top 10 worst
              <span className={styles.infoIcon} style={{ marginLeft: 6 }}>
                ℹ<span className={styles.infoTip}>
                  Top 10 kiosks with the highest waste count.
                </span>
              </span>
            </h4>
            <BarChart
              data={wasteData}
              width={Math.max(500, wasteData.length * 78)}
              height={320}
            />
          </div>
        )}
      </div>
      </>}

      {/* Data Grid */}
      <DataGrid name={GRID_NAME} onDataLoaded={handleDataLoaded} autoSearch extraColumnInfo={segmentFilterCol} hiddenColumns={hiddenColumns} hiddenFilters={hiddenFilters} highlightColumn={highlightColumn} highlightValue={highlightValue} />
    </>
  );
}
